/**
 * L'argent — **entiers, en centimes, partout** (ARCHITECTURE §3).
 *
 * Aucun flottant ne traverse le système : ni en base (`integer` PG), ni dans les
 * DTO, ni dans les calculs. Un montant est un `number` entier de centimes ;
 * 45.– vaut `4500`. Les seules conversions vers/depuis une chaîne se font ici.
 *
 * Format d'affichage suisse : `1'234.50` — apostrophe droite U+0027 comme
 * séparateur de milliers, point comme séparateur décimal, deux décimales
 * toujours (ARCHITECTURE §3, NF-07).
 */
import { AppError } from './erreurs.js';
/** Nombre de centimes dans un franc. */
export const CENTIMES_PAR_FRANC = 100;
/** Séparateur de milliers suisse : apostrophe droite (U+0027). */
export const SEPARATEUR_MILLIERS = "'";
/** Séparateur décimal suisse pour les montants. */
export const SEPARATEUR_DECIMAL = '.';
/** Code ISO de la devise. Mono-devise en v1 — aucune conversion. */
export const DEVISE = 'CHF';
/* -------------------------------------------------------------------------- */
/* Garde-fous                                                                 */
/* -------------------------------------------------------------------------- */
/** `true` si la valeur est un entier de centimes exploitable. */
export function estMontantValide(valeur) {
    return typeof valeur === 'number' && Number.isSafeInteger(valeur);
}
/**
 * Vérifie qu'un montant est bien un entier de centimes et le renvoie.
 * Lève `AppError.validation` (400) sinon — un flottant qui arrive jusqu'ici est
 * un bug d'appelant, pas une donnée à arrondir en silence.
 */
export function assertMontant(valeur, champ = 'montant') {
    if (!estMontantValide(valeur)) {
        throw AppError.validation(`Le champ « ${champ} » doit être un montant entier en centimes.`, {
            champ,
            valeur: String(valeur),
        });
    }
    return valeur;
}
/** Idem, en exigeant en plus un montant positif ou nul (colonnes `CHECK >= 0`). */
export function assertMontantPositif(valeur, champ = 'montant') {
    assertMontant(valeur, champ);
    if (valeur < 0) {
        throw AppError.validation(`Le champ « ${champ} » ne peut pas être négatif.`, {
            champ,
            valeur,
        });
    }
    return valeur;
}
/* -------------------------------------------------------------------------- */
/* Arithmétique sûre                                                          */
/* -------------------------------------------------------------------------- */
/** Somme de montants. Vérifie chaque terme et le total. */
export function somme(montants) {
    let total = 0;
    let index = 0;
    for (const montant of montants) {
        assertMontant(montant, `montants[${String(index)}]`);
        total += montant;
        index += 1;
    }
    return assertMontant(total, 'total');
}
/** `additionner(a, b, c)` — variante variadique de `somme`. */
export function additionner(...montants) {
    return somme(montants);
}
/** Différence de deux montants (le résultat peut être négatif : reste dû, avoir…). */
export function soustraire(a, b) {
    assertMontant(a, 'a');
    assertMontant(b, 'b');
    return assertMontant(a - b, 'resultat');
}
/**
 * Montant × quantité entière (n places au même tarif).
 * La quantité doit être un entier : pas de « 1.5 place ».
 */
export function multiplier(montant, quantite) {
    assertMontant(montant, 'montant');
    if (!Number.isSafeInteger(quantite) || quantite < 0) {
        throw AppError.validation('La quantité doit être un entier positif.', { quantite });
    }
    return assertMontant(montant * quantite, 'resultat');
}
/**
 * Applique un pourcentage à un montant, en arrondi commercial au centime
 * (`0.5` centime monte). Utile pour un éventuel rabais exprimé en %.
 */
export function pourcentage(montant, taux) {
    assertMontant(montant, 'montant');
    if (!Number.isFinite(taux)) {
        throw AppError.validation('Le taux doit être un nombre fini.', { taux });
    }
    return assertMontant(Math.round((montant * taux) / 100), 'resultat');
}
/**
 * Borne un montant dans `[0, plafond]`.
 * PAY-02 : une déduction (chèque culture) est plafonnée au montant restant dû.
 */
export function plafonner(montant, plafond) {
    assertMontant(montant, 'montant');
    assertMontantPositif(plafond, 'plafond');
    return Math.min(Math.max(montant, 0), plafond);
}
/** `a` et `b` désignent-ils exactement le même montant ? (au centime près) */
export function memeMontant(a, b) {
    return estMontantValide(a) && estMontantValide(b) && a === b;
}
/**
 * Formate des centimes au format suisse.
 *
 * ```ts
 * formatCHF(123450)                        // "1'234.50"
 * formatCHF(0)                             // "0.00"
 * formatCHF(-4500)                         // "-45.00"
 * formatCHF(4500, { avecDevise: true })    // "CHF 45.00"
 * formatCHF(5000, { signeExplicite: true })// "+50.00"
 * ```
 *
 * Volontairement écrit à la main plutôt qu'avec `Intl.NumberFormat('de-CH')` :
 * l'implémentation ICU utilise l'apostrophe typographique U+2019 selon les
 * versions de Node, alors que le format attendu ici est figé (U+0027).
 */
export function formatCHF(centimes, options = {}) {
    assertMontant(centimes, 'centimes');
    const negatif = centimes < 0;
    const absolu = Math.abs(centimes);
    const francs = Math.trunc(absolu / CENTIMES_PAR_FRANC);
    const reste = absolu % CENTIMES_PAR_FRANC;
    const entiere = grouperMilliers(francs);
    const decimale = String(reste).padStart(2, '0');
    const signe = negatif ? '-' : options.signeExplicite === true && centimes > 0 ? '+' : '';
    const nombre = `${signe}${entiere}${SEPARATEUR_DECIMAL}${decimale}`;
    return options.avecDevise === true ? `${DEVISE} ${nombre}` : nombre;
}
/**
 * Insère l'apostrophe suisse tous les trois chiffres : `1234567` → `1'234'567`.
 *
 * Exportée (pas seulement interne à `formatCHF`) parce que les nombres
 * « ordinaires » de l'application — un compte de sièges, un cumul de
 * représentations — doivent afficher le **même** séparateur que les montants.
 * `Intl.NumberFormat('fr-CH')` ne convient pas : son résultat dépend de la
 * version d'ICU embarquée par le moteur (apostrophe typographique sur
 * certaines, espace sur d'autres) — non déterministe, donc jamais garanti
 * cohérent avec `formatCHF`, qui lui est volontairement écrit à la main.
 */
export function grouperMilliers(entier) {
    const chiffres = String(Math.trunc(Math.abs(entier)));
    if (chiffres.length <= 3)
        return `${entier < 0 ? '-' : ''}${chiffres}`;
    const groupes = [];
    let fin = chiffres.length;
    while (fin > 3) {
        groupes.unshift(chiffres.slice(fin - 3, fin));
        fin -= 3;
    }
    groupes.unshift(chiffres.slice(0, fin));
    return `${entier < 0 ? '-' : ''}${groupes.join(SEPARATEUR_MILLIERS)}`;
}
/**
 * Variante « comptable » sans décimales quand le montant est rond, comme dans
 * les grilles tarifaires du théâtre : `4500` → `45.–`, `4550` → `45.50`.
 */
export function formatCHFCourt(centimes) {
    assertMontant(centimes, 'centimes');
    if (centimes % CENTIMES_PAR_FRANC === 0) {
        const negatif = centimes < 0;
        const francs = Math.abs(centimes) / CENTIMES_PAR_FRANC;
        return `${negatif ? '-' : ''}${grouperMilliers(francs)}.–`;
    }
    return formatCHF(centimes);
}
/* -------------------------------------------------------------------------- */
/* Analyse                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Convertit une saisie humaine en centimes. Tolérant sur la forme, strict sur
 * le fond : ce qui n'est pas un montant sans ambiguïté est refusé.
 *
 * Accepte : `"45"`, `"45.-"`, `"45.–"`, `"45.50"`, `"45,50"`, `"1'234.50"`,
 * `"1 234.50"`, `"1234.5"`, `"CHF 45.00"`, `"-45.00"`, `"45.5"`.
 * Refuse : `""`, `"abc"`, `"45.505"` (plus de deux décimales), `"1.2.3"`.
 *
 * @throws AppError VALIDATION (400)
 */
export function parseCHF(saisie, champ = 'montant') {
    const centimes = parseCHFOuNull(saisie);
    if (centimes === null) {
        throw AppError.validation(`« ${saisie} » n'est pas un montant valide.`, { champ, saisie });
    }
    return centimes;
}
/** Version non levante de `parseCHF` : renvoie `null` si la saisie est invalide. */
export function parseCHFOuNull(saisie) {
    if (typeof saisie !== 'string')
        return null;
    let texte = saisie.trim();
    if (texte === '')
        return null;
    // Devise éventuelle, en tête ou en queue.
    texte = texte.replace(/^chf/i, '').replace(/chf$/i, '').trim();
    // Signe.
    let signe = 1;
    if (texte.startsWith('-')) {
        signe = -1;
        texte = texte.slice(1).trim();
    }
    else if (texte.startsWith('+')) {
        texte = texte.slice(1).trim();
    }
    // Notation suisse « 45.– » / « 45.- » : zéro centime.
    texte = texte.replace(/[.,](?:–|—|-)$/u, '.00');
    // Séparateurs de milliers : apostrophe droite, apostrophe typographique et
    // toute espace (`\s` couvre l'insécable U+00A0 et la fine U+202F des tableurs).
    texte = texte.replace(/['’\s]/gu, '');
    // Séparateur décimal : la virgule vaut le point.
    texte = texte.replace(',', '.');
    if (!/^\d+(?:\.\d{1,2})?$/u.test(texte))
        return null;
    const [partieEntiere = '', partieDecimale = ''] = texte.split('.');
    const centimes = Number(partieEntiere) * CENTIMES_PAR_FRANC + Number(partieDecimale.padEnd(2, '0'));
    if (!Number.isSafeInteger(centimes))
        return null;
    return signe * centimes;
}
/** Convertit des francs (nombre décimal issu d'un tableur) en centimes. */
export function francsVersCentimes(francs) {
    if (!Number.isFinite(francs)) {
        throw AppError.validation('Montant illisible.', { francs: String(francs) });
    }
    // Passage par une chaîne à 2 décimales : évite 45.10 * 100 === 4509.999…
    return assertMontant(Math.round(Number(francs.toFixed(2)) * CENTIMES_PAR_FRANC), 'montant');
}
/**
 * Convertit des centimes en francs décimaux. **À n'utiliser que pour un export
 * CSV destiné à un tableur** — jamais pour un calcul ni pour une API.
 */
export function centimesVersFrancs(centimes) {
    assertMontant(centimes, 'centimes');
    return centimes / CENTIMES_PAR_FRANC;
}
/** La vente a déjà été recalculée à l’annulation ; l’abonnement sera rectifié par l’avoir.
 * On conserve d’abord les fonds nécessaires aux prestations qui restent dues. */
export function calculerRemboursementIncident(type, credit, net, encaisse) {
    const netApresAvoir = type === 'vente' ? net : Math.max(0, net - credit);
    return Math.min(credit, Math.max(0, encaisse - netApresAvoir));
}
//# sourceMappingURL=money.js.map